import React, { FunctionComponent } from 'react';
import ReactDOM from 'react-dom';
import { ErrorBoundary } from 'react-error-boundary';
import { setConfig } from 'react-hot-loader';
import { hot } from 'react-hot-loader/root';
import { QueryClient, QueryClientProvider } from 'react-query';
import { ReactQueryDevtools } from 'react-query/devtools';
import { BrowserRouter } from 'react-router-dom';
import pkg from '../package.json';
import MainLayout from './foundations';
import Authentication from './foundations/Authentication';
import ThemeContextComponent from './foundations/ThemeContext';
import { AlertDialogContextComponent } from './hooks/useAlertDialog';
import { BackdropContextComponent } from './hooks/useBackdrop';
import { ConfirmDialogContextComponent } from './hooks/useConfirmDialog';
import { SnackbarContextComponent } from './hooks/useSnackbar';
import { StorySelectContextComponent } from './hooks/useStorySelect';

setConfig({
  reloadHooks: false,
});

const providers = [
  StorySelectContextComponent,
  SnackbarContextComponent,
  BackdropContextComponent,
  ConfirmDialogContextComponent,
  AlertDialogContextComponent,
  BrowserRouter,
  Authentication,
];

const CombinedProviders = ({ children }: { children: JSX.Element }): JSX.Element =>
  providers.reverse().reduce<JSX.Element>((components, P) => <P>{components}</P>, children);

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
    },
  },
});

const ErrorFallback = (): JSX.Element => (
  <div className="loading-bg">
    <p id="adminPreloadMessage">
      ⚠ An error has occurred ⚠<br />
      Please contact the product team. Sorry for the inconvenience.
    </p>
  </div>
);

const App: FunctionComponent = () => {
  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <ThemeContextComponent>
        <CombinedProviders>
          <QueryClientProvider client={queryClient}>
            <MainLayout />
            <ReactQueryDevtools />
          </QueryClientProvider>
        </CombinedProviders>
      </ThemeContextComponent>
    </ErrorBoundary>
  );
};

const HotModuleReplacement = hot(App);
ReactDOM.render(<HotModuleReplacement />, document.getElementById('root'));
